import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import LoginPage from "../../../Login/LoginPage";
import { Card, Space, Typography, Select, Button, message, Input } from "antd";
import { isLogged, getAuthToken } from "../../../../helpers/account";
import { api } from "../../../../config/api";
import { Helmet } from "react-helmet-async";

const { Title, Text } = Typography;
const { TextArea } = Input;

function AccountCategoriesPage() {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [tags, setTags] = useState([]);
    const [selectedTags, setSelectedTags] = useState([]);
    const [accountTags, setAccountTags] = useState([]);
    const [accountDescription, setAccountDescription] = useState("");

    useEffect(() => {
        if (isLogged()) {
            // Fetch all available tags
            fetch(`${api.host}/tags`, {
                method: "GET",
                headers: new Headers({
                    "Content-Type": "application/json",
                    Authorization: `${getAuthToken()}`,
                }),
            })
                .then((response) => response.json())
                .then((data) => {
                    const tagOptions = data.map((tag) => ({
                        value: tag.uuid,
                        label: tag.name,
                    }));
                    setTags(tagOptions);
                })
                .catch((error) => {
                    console.error("Error fetching all tags:", error);
                });

            // Fetch account tags
            fetch(`${api.host}/tags/account`, {
                method: "GET",
                headers: new Headers({
                    "Content-Type": "application/json",
                    Authorization: `${getAuthToken()}`,
                }),
            })
                .then((response) => response.json())
                .then((data) => {
                    const selectedTagValues = data.map(tag => tag.uuid);
                    setAccountTags(data);
                    setSelectedTags(selectedTagValues);
                })
                .catch((error) => {
                    console.error("Error fetching account tags:", error);
                });

            // Fetch account description
            fetch(`${api.host}/accounts/${sessionStorage.getItem("uuid")}`, {
                method: "GET",
                headers: new Headers({
                    "Content-Type": "application/json",
                    Authorization: `${getAuthToken()}`,
                }),
            })
                .then((response) => response.json())
                .then((data) => {
                    setAccountDescription(data.description || "");
                    setLoading(false);
                })
                .catch((error) => {
                    console.error("Error fetching account description:", error);
                    setLoading(false);
                });
        }
    }, []);

    const handleTagChange = (value) => {
        setSelectedTags(value);
    };

    const updateTags = () => {
        fetch(`${api.host}/tags/account`, {
            method: "PUT",
            headers: new Headers({
                "Content-Type": "application/json",
                Authorization: `${getAuthToken()}`,
            }),
            body: JSON.stringify({
                tagUuids: selectedTags,
            }),
        })
            .then((response) => response.json())
            .then((data) => {
                message.success(t('account.profile.tagsListSaved'));
            })
            .catch((error) => {
                console.error("Error updating tags:", error);
            });
    };

    const handleDescriptionChange = (e) => {
        setAccountDescription(e.target.value);
    };

    const saveDescription = () => {
        fetch(`${api.host}/accounts/${sessionStorage.getItem("uuid")}`, {
            method: "PUT",
            headers: new Headers({
                "Content-Type": "application/json",
                Authorization: `${getAuthToken()}`,
            }),
            body: JSON.stringify({
                description: accountDescription,
            }),
        })
            .then((response) => response.json())
            .then((data) => {
                message.success(t('account.profile.descriptionSaved'));
            })
            .catch((error) => {
                console.error("Error saving account description:", error);
            });
    };

    if (isLogged()) {
        return (
            <>
                <Helmet>
                    <title>{t('account.profile.title')} - Taskizy</title>
                </Helmet>
                
                <div style={{ background: "#f8f9fa", minHeight: "100vh", padding: "40px 20px" }}>
                    <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
                        {/* Header */}
                        <div style={{ marginBottom: "32px" }}>
                            <Title level={2} style={{ margin: 0, color: "#2c3e50" }}>
                                {t('account.profile.myProfile')}
                            </Title>
                            <Text style={{ color: "#6c757d", fontSize: "16px" }}>
                                {t('account.profile.manageProfile')}
                            </Text>
                        </div>

                        <Space direction="vertical" size="large" style={{ width: "100%" }}>
                            {/* Tags Section */}
                            <Card
                                title={
                                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                        <span>🏷️</span>
                                        <span>{t('account.profile.yourSkills')}</span>
                                    </div>
                                }
                                style={{
                                    border: "none",
                                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                                    borderRadius: "12px",
                                }}
                            >
                                <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                                    <Text style={{ color: "#6c757d", lineHeight: "1.6" }}>
                                        {t('account.profile.skillsDescription')}
                                    </Text>
                                    <Select
                                        mode="multiple"
                                        style={{ width: "100%" }}
                                        placeholder={t('account.profile.selectSkillTags')}
                                        onChange={handleTagChange}
                                        value={selectedTags}
                                        options={tags}
                                        size="large"
                                    />
                                    <Button 
                                        type="primary" 
                                        onClick={updateTags}
                                        style={{ borderRadius: "8px" }}
                                    >
                                        {t('account.profile.saveTags')}
                                    </Button>
                                </Space>
                            </Card>

                            {/* Description Section */}
                            <Card
                                title={
                                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                        <span>📝</span>
                                        <span>{t('account.profile.profileDescription')}</span>
                                    </div>
                                }
                                style={{
                                    border: "none",
                                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                                    borderRadius: "12px",
                                }}
                            >
                                <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                                    <Text style={{ color: "#6c757d", lineHeight: "1.6" }}>
                                        {t('account.profile.descriptionHelp')}
                                    </Text>
                                    <TextArea
                                        maxLength={500}
                                        rows={6}
                                        value={accountDescription}
                                        onChange={handleDescriptionChange}
                                        placeholder={t('account.profile.descriptionPlaceholder')}
                                        showCount
                                        style={{ borderRadius: "8px" }}
                                    />
                                    <Button 
                                        type="primary" 
                                        onClick={saveDescription}
                                        style={{ borderRadius: "8px" }}
                                    >
                                        {t('account.profile.saveDescription')}
                                    </Button>
                                </Space>
                            </Card>

                            {/* Help Card */}
                            <Card 
                                title={`💡 ${t('profile.tip')}`}
                                style={{ 
                                    borderRadius: "12px", 
                                    border: "none", 
                                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)", 
                                    background: "#f8f9fa" 
                                }}
                            >
                                <Text style={{ color: "#6c757d", fontSize: "14px", lineHeight: "1.6" }}>
                                    {t('account.profile.tipText')}
                                </Text>
                            </Card>
                        </Space>
                    </div>
                </div>
            </>
        );
    } else {
        return <LoginPage />;
    }
}

export default AccountCategoriesPage;